import React, { useState } from "react";
import LandingSection from "../components/LandingSection";
import InfoIcon from "@mui/icons-material/Info";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { Table, Container, Row, Col, Card, Button } from "react-bootstrap";
import "../styles/UtahBudget.css";
import Footer from "../components/Footer";
import { Pie } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import SocialServicesTable from "./SocialServicesTable";
import SocialServicesBudget from "./SocialServicesState";
import GlamBudgetChart from "../components/Budget/allfundschart";
import Budget2026Chart from "../components/Budget/Budget2026Chart";
import PublicEduBudget from "../components/Budget/PublicEduBudget";
import TransportationBudget from "../components/Budget/TransportationBudget";
import GenGovBudget from "../components/Budget/GenGovBudget";
import BuildingDebt from "../components/Budget/BuildingDebt";
import LawAndEnforcement from "../components/Budget/LawAndEnforcement";
import HigherEducation from "../components/Budget/HigherEducation";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.3, ease: "easeOut" },
};

// ---------- Helpers ----------
const fmtB = (n) => `${Number(n).toFixed(1)}B`;
const fmtPct = (n) => `${Number(n).toFixed(0)}%`;
// Format number with comma and ensure proper alignment with .00
const fmtAmount = (n) => {
  const num = Number(n);
  // Always format to 2 decimal places for proper alignment
  const formatted = num.toFixed(2);
  // Split into integer and decimal parts for comma formatting
  const parts = formatted.split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];
  // Add commas to integer part
  const integerWithCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${integerWithCommas}.${decimalPart}`;
};

// ---------- All Funds (FY 2026) ----------
const fundingSourcesAllFunds = [
  {
    name: "Category",
    amount: "Amount (Billion $)",
    pct: "% of Total Budget",
    bgcolor: "#6E11B090",
  },
  { name: "Federal Funds", amount: 8.3, pct: 27, bgcolor: "#6E11B090" },
  { name: "Income Tax Fund", amount: 6.6, pct: 21, bgcolor: "#E69BC690" },
  { name: "Other Funds", amount: 4.9, pct: 16, bgcolor: "#8064A290" },
  { name: "General Fund", amount: 3.7, pct: 12, bgcolor: "#4683B190" },
  { name: "Dedicated Credits", amount: 2.3, pct: 8, bgcolor: "#E6B1B190" },
  {
    name: "Transportation Investment Fund",
    amount: 2.2,
    pct: 7,
    bgcolor: "#EDE3B790",
  },
  {
    name: "Local Education Revenue",
    amount: 1.8,
    pct: 6,
    bgcolor: "#6ED5BE90",
  },
  { name: "Transportation Fund", amount: 0.9, pct: 3, bgcolor: "#DCE28B90" },
  { name: "Total", amount: 30.8, pct: 100, bgcolor: "#DCE28B90" },
];

const expenditureAreasAllFunds = [
  { name: "Social Services", amount: 10.4, pct: 34, link: "#services" },
  { name: "Public Education", amount: 8.6, pct: 28, link: "#public_education" },
  { name: "Transportation", amount: 4.0, pct: 13, link: "#transportation" },
  {
    name: "Higher Education",
    amount: 3.2,
    pct: 10,
    link: "#higher_transportation",
  },
  { name: "General Government", amount: 2.6, pct: 8, link: "#gen_gov" },
  { name: "Law Enforcement", amount: 1.5, pct: 5, link: "#law_enforcement" },
  { name: "Buildings & Debt", amount: 0.6, pct: 2, link: "#buildings_debt" },
];

const totalAmount = expenditureAreasAllFunds.reduce(
  (sum, row) => sum + Number(row.amount),
  0
);

// ✅ chartData
const expChartData = {
  labels: expenditureAreasAllFunds.map((r) => r.name),
  datasets: [
    {
      label: "Funding Amount (in Billion)",
      data: expenditureAreasAllFunds.map((r) => r.amount),
      backgroundColor: [
        "#38761D",
        "#3D85C6",
        "#F4CCCC",
        "#B6D7A8",
        "#8E7CC3",
        "#46BDC6",
        "#3C78D8",
      ],
      borderColor: "#fff",
      borderWidth: 2,
      hoverOffset: 15,
    },
  ],
};

// ✅ Corrected chart options
const expOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    datalabels: {
      display: true,
      color: "#000",
      font: {
        size: 14,
      },
      formatter: function (value, ctx) {
        const total = ctx.chart._metasets[0].total;
        const percent = ((value / total) * 100).toFixed(2);
        return "$" + value.toFixed(2);
      },
      textShadowBlur: 4,
      textShadowColor: "rgba(0,0,0,0.6)", // improves visibility
      anchor: "middle",
      align: "right",
    },
    legend: {
      position: "bottom",
      labels: {
        color: "#111",
        font: { size: 12 },
      },
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const label = context.label || "";
          const value = context.parsed || 0;
          const total = context.chart._metasets[0].total || 0;
          const percent = Math.round((value / total) * 100);
          return ` ${label} - ${value.toFixed(2)} (${percent}%)`;
        },
      },
    },
  },
};

// ---------- Highlights / Headlines ----------
const highlights = [
  {
    label: "Federal Funds",
    value: "$8.30 Billion",
    color: "#6E11B0",
  },
  {
    label: "Overlap Funds",
    value: "$10.70 Billion",
    color: "#ea9999",
  },
  {
    label: "State Funds",
    value: "$11.80 Billion",
    color: "#D62828",
  },
];

const BudgetViewToggle = ({ viewMode, setViewMode }) => (
  <div className="budget-view-toggle" role="group" aria-label="Budget view mode">
    <button
      type="button"
      className={`budget-view-toggle__button ${
        viewMode === "graphs" ? "is-active" : ""
      }`}
      onClick={() => setViewMode("graphs")}
      aria-pressed={viewMode === "graphs"}
    >
      Graphs
    </button>
    <button
      type="button"
      className={`budget-view-toggle__button ${
        viewMode === "tables" ? "is-active" : ""
      }`}
      onClick={() => setViewMode("tables")}
      aria-pressed={viewMode === "tables"}
    >
      Tables
    </button>
  </div>
);

const UtahBudget = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [activeTab, setActiveTab] = useState("Social Services");
  const [viewMode, setViewMode] = useState("graphs");

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <div>
      <LandingSection
        data={{
          imageName: "utah_budget.jpg",
          title: "Utah Budget",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah’s Budget: Building a Balanced Future</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah’s state budget reflects a commitment to fiscal responsibility
              and long-term growth. Each year, the legislature allocates funds
              across key areas such as education, infrastructure, healthcare,
              and economic development, ensuring resources are managed
              efficiently while maintaining one of the nation’s strongest
              economies.
            </p>
          </div>
        </div>
      </div>

      <div className="min-h-screen w-full text-black">
        <section className="max-w-7xl mx-auto px-4 pt-10 items-center text-center">
          {" "}
          <h1 className="font-extrabold">Utah Budget — FY 2026</h1>
        </section>
        <div
          className="bg-green-50"
          style={{
            backgroundImage: `url('/img/grid_image.png')`,
          }}
        >
          <header className="max-w-7xl mx-auto px-4 pt-10 pb-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="font-bold tracking-tight">
                  Total (All Funds) - FY 2026
                  <span style={{ color: "#4a86e8" }}>
                    &nbsp;($30.8 Billion)
                  </span>
                </h3>
                <p className="mt-2 text-sm md:text-base opacity-80">
                  A quick, visual summary of funding sources, expenditure by
                  area, and key highlights.
                </p>
              </div>
              <div className="flex flex-col items-start gap-3 md:items-end">
                <BudgetViewToggle
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                />
                <a
                  href="https://le.utah.gov/interim/2025/pdf/00002339.pdf"
                  target="_blank"
                  className="text-right"
                >
                  Data Source
                </a>
              </div>
            </div>
          </header>

          <section className="max-w-7xl mx-auto px-4 pb-2 items-center">
            <InfoIcon style={{ color: "#3c78d8" }} />{" "}
            <span>
              <b>The FY 2026 Total (All Funds) Budget</b> is distributed across
              the following <b>three</b> fund categories.
            </span>
          </section>

          {/* KPI Cards */}
          <section className="max-w-7xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-center">
            {highlights.map((h, i) => (
              <div
                key={i}
                style={{ backgroundColor: h.color, color: "#fff" }}
                className={`rounded-2xl backdrop-blur border border-white/10 p-4 shadow flex flex-col justify-between`}
              >
                <div className="text-xs uppercase tracking-wider">
                  {h.label}
                </div>
                <div className="mt-1 text-2xl font-bold">{h.value}</div>
              </div>
            ))}
          </section>

          <section className="max-w-7xl mx-auto px-4 mt-10 py-10">
            <div className="rounded-2xl bg-white border border-white/10 p-4 shadow">
              <h3>Utah Budget FY 2026</h3>

              <Row className="gy-4 flex items-center">
                {/* Table */}
                <Col md={12}>
                  {/* <GlamBudgetChart /> */} <Budget2026Chart />
                  <p className="mt-2 text-sm md:text-base opacity-80 flex gap-2">
                    <span>*</span>{" "}
                    <span>
                      <b>Federal funds</b> comprise about <b>$8.30 Billion,</b>{" "}
                      or roughly <b>27%</b> of the budget, primarily for social
                      services, Medicaid, housing, and community programs.
                    </span>
                  </p>
                  <p className="mt-2 text-sm md:text-base opacity-80 flex gap-2 ">
                    <span>*</span>{" "}
                    <span>
                      <b>State funds</b> total <b>$11.80 Billion,</b> covering
                      General Fund, Income Tax Fund, and Uniform School Fund
                      appropriations.
                    </span>
                  </p>
                  <p className="mt-2 text-sm md:text-base opacity-80 flex gap-2">
                    <span>*</span>{" "}
                    <span>
                      <b>Overlap funds</b> represent the remaining{" "}
                      <b>$10.70 billion,</b> including restricted funds,
                      enterprise funds, and other non-general/non-federal
                      sources.
                    </span>
                  </p>
                </Col>
              </Row>
            </div>
          </section>
        </div>

        <div
          className="bg-purple-50"
          style={{
            backgroundImage: `url('/img/grid_image.png')`,
          }}
        >
          {" "}
          <section className="max-w-7xl mx-auto px-4 p-10">
            {" "}
            <div className="rounded-2xl bg-white border border-white/10 p-4 shadow">
              <h3>Funding Sources of Utah Budget FY 2026</h3>

              <Row className="gy-4 flex items-center">
                {viewMode === "tables" && (
                  <Col md={12}>
                    <div className="overflow-x-auto flex items-center">
                      <table className="min-w-full table-fixed border border-gray-400 text-center mt-8">
                        <thead>
                          <tr className="bg-gray-100">
                            {fundingSourcesAllFunds.map((item, i) => (
                              <th
                                key={i}
                                className="border border-gray-400 px-2 py-2 text-sm w-1/6"
                                style={{ backgroundColor: "#bfdbfe" }}
                              >
                                {item.name === "Total" ? (
                                  <>&nbsp;&nbsp;Total&nbsp;&nbsp;</>
                                ) : (
                                  item.name
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>

                        <tbody>
                          <tr>
                            {fundingSourcesAllFunds.map((item, i) => (
                              <td
                                key={i}
                                className={`border border-gray-400 py-2 font-medium w-1/6 ${
                                  i === fundingSourcesAllFunds.length - 1
                                    ? "bg-blue-50 font-semibold"
                                    : ""
                                }`}
                              >
                                {item.amount === "Amount (Billion $)"
                                  ? item.amount
                                  : `$${fmtAmount(item.amount)}`}
                              </td>
                            ))}
                          </tr>

                          <tr className="bg-gray-50 font-semibold text-gray-700">
                            {fundingSourcesAllFunds.map((item, i) => (
                              <td
                                key={i}
                                className={`border border-gray-400 py-2 font-medium w-1/6 ${
                                  i === fundingSourcesAllFunds.length - 1
                                    ? "bg-blue-50 font-semibold"
                                    : ""
                                }`}
                              >
                                {item.pct === "% of Total Budget"
                                  ? item.pct
                                  : `${item.pct.toLocaleString()}%`}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="mt-3">
                      The exact total of <b>all funds</b> is{" "}
                      <b>$30,844,859,600 </b>
                      confirming that the rounded presentation aligns without
                      any substantive discrepancies. Budget visuals are rounded
                      for clarity, and no categories are missing from the
                      listed breakdown.
                    </p>
                  </Col>
                )}

                {viewMode === "graphs" && (
                  <Col md={12}>
                    <GlamBudgetChart />
                    <p className="mt-2 text-sm md:text-base opacity-80 flex gap-2">
                      <span>*</span>{" "}
                      <span>
                        <b>Other Funds</b> includes Rainy day funds, disaster
                        recovery, wildland fire suppression, and program-specific
                        reserves.
                      </span>
                    </p>
                  </Col>
                )}
              </Row>
            </div>
          </section>
        </div>

        <div
          className="bg-red-50"
          style={{
            backgroundImage: `url('/img/grid_image.png')`,
          }}
        >
          <section className="max-w-7xl mx-auto px-4 p-10">
            <div className="rounded-2xl bg-white border border-white/10 p-4 shadow">
              <h3>Expenditure Areas of Utah Budget FY 2026</h3>

              <Row className="gy-4 flex items-center">
                {viewMode === "tables" && (
                  <Col md={12}>
                    <div className="overflow-x-auto flex items-center">
                      <table className="w-full text-center border-collapse mt-4">
                        <thead>
                          <tr>
                            <th
                              className="p-2 text-black border"
                              style={{ backgroundColor: "#bfdbfe" }}
                            >
                              Category
                            </th>
                            {expenditureAreasAllFunds.map((r) => (
                              <th
                                key={r.name}
                                className="p-2 text-black border"
                                style={{ backgroundColor: "#bfdbfe" }}
                              >
                                {r.name}
                              </th>
                            ))}
                            <th
                              className="p-2 text-black border"
                              style={{ backgroundColor: "#bfdbfe" }}
                            >
                              <>&nbsp;&nbsp;Total&nbsp;&nbsp;</>
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          <tr>
                            <td className="p-2 border font-semibold">
                              Amount (Billion $)
                            </td>
                            {expenditureAreasAllFunds.map((r) => (
                              <td
                                key={r.name}
                                className="p-2 border font-semibold"
                              >
                                ${fmtAmount(r.amount)}
                              </td>
                            ))}
                            <td className="p-2 border font-semibold bg-blue-50">
                              $30.80
                            </td>
                          </tr>
                          <tr className="bg-gray-50 font-semibold text-gray-700">
                            <td className="border border-gray-400 py-2 font-medium">
                              % of Total Budget
                            </td>
                            {expenditureAreasAllFunds.map((item, i) => (
                              <td
                                key={i}
                                className="border border-gray-400 py-2 font-medium"
                              >
                                {item.pct === "% of Total Budget"
                                  ? item.pct
                                  : `${item.pct.toLocaleString()}%`}
                              </td>
                            ))}
                            <td className="border border-gray-400 py-2 font-medium bg-blue-50">
                              100%
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Col>
                )}

                {viewMode === "graphs" && (
                  <Col md={12}>
                    <div className="p-3 bg-white rounded-3 border">
                      <h2 className="text-xl font-semibold text-center">
                        Expenditure Areas of Utah Budget FY 2026
                      </h2>
                      <div
                        style={{
                          height: "400px",
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Pie data={expChartData} options={expOptions} />
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-4 pt-10 items-center text-center">
            {" "}
            <h1 className="text-3xl md:text-3xl font-extrabold">
              Expenditure Areas and Allocations of Utah Budget FY 2026
            </h1>
          </section>

          <section className="max-w-7xl mx-auto px-4 pt-10 items-center text-center">
            <p className="text-sm md:text-base opacity-80 items-center justify-center flex gap-2">
              <InfoIcon style={{ color: "#3c78d8" }} />{" "}
              <span>
                Click any category tab to explore its detailed breakdown.
              </span>
            </p>{" "}
            <div className="tabs-wrapper">
              {/* ◀ Left */}
              <button
                className="tab-scroll-btn left"
                onClick={() =>
                  document.getElementById("tabs-scroll-inner")?.scrollBy({
                    left: -200,
                    behavior: "smooth",
                  })
                }
              >
                ‹
              </button>

              {/* Tabs */}
              <div id="tabs-scroll-inner" className="tabs-scroll">
                <Tabs
                  id="expenditure"
                  activeKey={activeTab}
                  onSelect={(tab) => setActiveTab(tab)}
                  variant="tabs"
                  className="browser-tabs"
                >
                  {expenditureAreasAllFunds.map((item) => (
                    <Tab
                      key={item.name}
                      eventKey={item.name}
                      title={item.name}
                    />
                  ))}
                </Tabs>
              </div>

              {/* ▶ Right */}
              <button
                className="tab-scroll-btn right"
                onClick={() =>
                  document.getElementById("tabs-scroll-inner")?.scrollBy({
                    left: 200,
                    behavior: "smooth",
                  })
                }
              >
                ›
              </button>
            </div>
          </section>

          <div className="tab-content max-w-7xl mx-auto px-4 pb-4">
            {activeTab === "Social Services" && (
              <div className="rounded-2xl bg-white p-4 shadow mb-10">
                <SocialServicesTable viewMode={viewMode} />
                <p className="flex gap-2 p-4">
                  <span>*</span>
                  <span>
                    <b>Utah's FY 2026 Social Service Budget $10,400 million</b>{" "}
                    represents the largest portion of state spending, ensuring
                    the stability of the vital Medicaid program while making
                    targeted, ongoing investments to reduce the disability
                    services waiting list and support Utah families through tax
                    credits and direct care.{" "}
                  </span>
                </p>
              </div>
            )}

            {activeTab === "Public Education" && (
              <div className="rounded-2xl bg-white p-4 shadow mb-10">
                <PublicEduBudget viewMode={viewMode} />
                <p className="flex gap-2 p-4">
                  <span>*</span>
                  <span>
                    <b>Other/Debt Service</b> includes Programs like the Utah
                    Schools for the Deaf and Blind, administration costs, and
                    debt service payments.
                  </span>
                </p>
              </div>
            )}

            {activeTab === "Transportation" && (
              <div className="rounded-2xl bg-white p-4 shadow mb-10">
                <TransportationBudget viewMode={viewMode} />
                <p className="flex gap-2 p-4">
                  <span>*</span>
                  <span>
                    <b>Utah's FY 2026 Transportation Budget $4000 million</b> is
                    heavily invested in expanding highway capacity and
                    preserving existing infrastructure across the state, with a
                    substantial portion dedicated to funding key public transit
                    projects and shoring up capital funds for road construction.
                  </span>
                </p>
              </div>
            )}

            {activeTab === "Higher Education" && (
              <div className="rounded-2xl bg-white p-4 shadow mb-10">
                <HigherEducation viewMode={viewMode} />
                <p className="flex gap-2 p-4">
                  <span>*</span>{" "}
                  <span>
                    <b>
                      Utah's FY 2026 Higher Education Budget allocates $3200
                      million
                    </b>{" "}
                    supporting operating enhancements, performance funding,
                    workforce programs, and major capital projects.
                  </span>
                </p>
              </div>
            )}

            {activeTab === "General Government" && (
              <div className="rounded-2xl bg-white p-4 shadow mb-10">
                <GenGovBudget viewMode={viewMode} />
                <p className="flex gap-2 p-4">
                  <span>*</span>
                  <span>
                    <b>
                      Utah's FY 2026 General Government Budget $2600 million
                    </b>{" "}
                    focuses on efficient state operations, securing the
                    workforce with a <b>2.5%</b> compensation increase,
                    accelerating strategic investments in water and energy (like
                    Operation Gigawatt), and delivering significant tax relief
                    to Utah families and seniors.
                  </span>
                </p>
              </div>
            )}

            {activeTab === "Law Enforcement" && (
              <div className="rounded-2xl bg-white p-4 shadow mb-10">
                <LawAndEnforcement viewMode={viewMode} />
                <p className="flex gap-2 p-4">
                  <span>*</span>{" "}
                  <span>
                    <b>Utah's FY 2026 Law Enforcement Budget $1500 million</b>{" "}
                    represents the total funding for the large agencies
                    (Department of Corrections, Department of Public Safety, and
                    the Judiciary) and various state agencies and programs.{" "}
                  </span>
                </p>
              </div>
            )}

            {activeTab === "Buildings & Debt" && (
              <div className="rounded-2xl bg-white p-4 shadow mb-10">
                <BuildingDebt viewMode={viewMode} />
                <p className="flex gap-2 p-4">
                  <span>*</span>
                  <span>
                    <b>Utah's FY 2026 Buildings & Debt Budget $600 million</b>{" "}
                    focused on maintaining Utah's fiscal strength through a
                    substantial one-time paydown of over <b>$255 million</b> in
                    existing debt. Also funding one-time and ongoing capital
                    improvements for state and higher education facilities.
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default UtahBudget;
